import i18n, { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import LngDetector from 'i18next-browser-languagedetector';
import intervalPlural from 'i18next-intervalplural-postprocessor';
import { getCookieDomain } from './utils/cookieDomain';

// --- CONFIG ---
// Increment this version to force-flush client caches
const TRANSLATION_VERSION = '4.8.1'; 

const getLangFromCookie = (): string | undefined => {
  try {
    const cookieString = document.cookie.split('; ').find((row) => row.startsWith('session='));
    if (!cookieString) return undefined;

    const base64Json = cookieString.split('=')[1];
    const decodedJson = atob(base64Json);
    const sessionData = JSON.parse(decodedJson);
    const jwt = sessionData.jwt;
    if (!jwt) return undefined;

    const jwtPayloadBase64 = jwt.split('.')[1];
    const decodedJwtPayload = atob(jwtPayloadBase64);
    const userPayload = JSON.parse(decodedJwtPayload);

    return userPayload?.preferences?.language?.langName || undefined;
  } catch (e) {
    return undefined;
  }
};

const customDetector = {
  name: 'customDetector',
  lookup() {
    return getLangFromCookie();
  },
};

const options: InitOptions = {
  fallbackLng: 'en',
  defaultNS: 'common', 
  fallbackNS: 'common',
  debug: process.env.NODE_ENV === 'development',
  keySeparator: '.', 
  ns: [
      'common', 'auth', 'billing', 'companies', 'companyPage', 'accountSetup', 'job', 'onboarding',
      'organization-data', 'organization-media', 'organization-workplace',
      'dashboard', 'rec_dashboard', 'mgmt_dashboard', 'navigation', 'layout', 'plansPage', 'getPremium', 'awards'
  ], 
  interpolation: {
    escapeValue: false,
  },
  detection: {
    order: ['customDetector', 'cookie', 'localStorage', 'navigator'],
    lookupCookie: 'noviopus-guest-language',
    caches: ['localStorage', 'cookie'],
    cookieDomain: getCookieDomain(),
  },
  react: {
    wait: true, 
    useSuspense: false, 
    defaultTransParent: 'div',
  },
};

const languageDetector = new LngDetector();
languageDetector.addDetector(customDetector);

const fetchPromises: { [language: string]: Promise<any> } = {};

i18n
  .use(languageDetector)
  .use(intervalPlural)
  .use(
    resourcesToBackend((language, namespace, callback) => {
      // 1. Version Check & Cache Load
      try {
        const cachedVersion = localStorage.getItem('translation_version');
        
        // If version mismatch, clear cache immediately
        if (cachedVersion !== TRANSLATION_VERSION) {
            console.log('[i18n] Translation version mismatch. Clearing cache.');
            localStorage.removeItem('translation');
            localStorage.setItem('translation_version', TRANSLATION_VERSION);
        } else {
            const translationStore = localStorage.getItem('translation');
            if (translationStore) {
              const parsedStore = JSON.parse(translationStore);
              if (parsedStore[language] && parsedStore[language][namespace]) {
                callback(null, parsedStore[language][namespace]);
                return;
              }
            }
        }
      } catch (e) {
        console.error('Failed to parse translations from localStorage', e);
        localStorage.removeItem('translation');
      }

      // 2. Fetch from Backend
      if (!fetchPromises[language]) {
        const url = `${process.env.REACT_APP_BASE_API}/business/${language}?v=${new Date().getTime()}`;
        
        fetchPromises[language] = fetch(url, {
            headers: { 'Pragma': 'no-cache', 'Cache-Control': 'no-cache' }
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Failed to fetch translations for ${language}`);
            return response.json();
          })
          .then((fullResource) => {
            try {
              const existingLangsJSON = localStorage.getItem('translation');
              const existingLangs = existingLangsJSON ? JSON.parse(existingLangsJSON) : {};
              existingLangs[language] = fullResource[language] || fullResource; 
              localStorage.setItem('translation', JSON.stringify(existingLangs));
            } catch (e) {
              console.warn('Failed to update localStorage with new translations');
            }
            return fullResource;
          })
          .catch((error) => {
            delete fetchPromises[language]; 
            throw error;
          });
      }

      fetchPromises[language]
        .then((resource) => {
          const languageResources = resource[language] || resource;
          const namespaceResources = languageResources[namespace] || {};
          callback(null, namespaceResources);
        })
        .catch((error) => {
          callback(error, null);
        });
    }),
  )
  .use(initReactI18next)
  .init(options);

export default i18n;